import React, { useState } from "react";
import { Mail, MessageSquare, Send, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Helmet } from "react-helmet-async";

export function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("Feedback");
  const [message, setMessage] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !message) {
      setErrorMsg("Please fill out all required fields.");
      return;
    }
    
    // Simulate API request
    setIsSubmitted(true);
    setErrorMsg("");
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Contact Us – 247 DevTools Hub</title>
        <meta name="description" content="Get in touch with 247 DevTools Hub. Submit feedback, tool requests, or ask security questions about our browser utilities." />
      </Helmet>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden grid md:grid-cols-5">
        
        {/* Sidebar Info */}
        <div className="md:col-span-2 bg-slate-900 text-white p-8 flex flex-col justify-between">
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Get in Touch</h2>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">
                Have a feature request? Want to report a bug, or inquire about custom tools? Drop us a line!
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-white">General Support</h4>
                  <p className="text-xs text-slate-400">support@247devtoolshub.com</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <MessageSquare className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-white">Community & Bugs</h4>
                  <p className="text-xs text-slate-400">Open an issue on GitHub</p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-6 text-xs text-slate-500">
            © 247 DevTools Hub. Operates fully locally in-RAM.
          </div>
        </div>

        {/* Contact Form */}
        <div className="md:col-span-3 p-8">
          {isSubmitted ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-10 space-y-4">
              <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">Message Sent!</h3>
              <p className="text-slate-500 text-sm max-w-sm">
                Thank you, <strong>{name}</strong>. Your message was successfully submitted. We appreciate your valuable suggestions to improve the Hub!
              </p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsSubmitted(false);
                  setName("");
                  setEmail("");
                  setMessage("");
                }}
              >
                Send Another Message
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <h3 className="text-xl font-bold text-slate-900">Send a Message</h3>
              
              {errorMsg && (
                <div className="p-3 bg-red-50 text-red-800 text-xs rounded-lg flex items-center gap-2 border border-red-100">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span>{errorMsg}</span>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Your Name *</label>
                  <Input 
                    placeholder="Alice Johnson" 
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email Address *</label>
                  <Input 
                    type="email" 
                    placeholder="alice@example.com" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Topic</label>
                <select 
                  value={subject} 
                  onChange={(e) => setSubject(e.target.value)} 
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-3 py-2 outline-none focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="Feedback">General Feedback</option>
                  <option value="ToolRequest">Request a New Tool</option>
                  <option value="BugReport">Report a Bug / Mistake</option>
                  <option value="Security">Security & Privacy Audit</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Message *</label>
                <textarea 
                  rows={4} 
                  placeholder="Tell us what you'd like to improve or request..." 
                  value={message} 
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm px-3 py-2 outline-none focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>

              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white border-none py-2 px-4 rounded-lg flex items-center justify-center gap-2">
                <Send className="w-4 h-4" />
                Submit Message
              </Button>
            </form>
          )}
        </div>

      </div>
    </div>
  );
}
